<script setup lang="ts">
import {useStorage} from '@vueuse/core'
import {isEmpty} from "lodash";
import {computed, onMounted, type Ref, ref, watch } from "vue";
import Input from "../../../backend/ui/Input.vue";
import type {AddressResponse} from "../../../responses/AddressResponse.ts";
import type {DebtorContactResponse} from "../../../responses/DebtorContactResponse.ts";
import {Money} from "../../../utils/money.ts";
import {label} from '../../mixins/translations.js';
import {useCartStore} from "../../stores/cart";
import AddressEntityInput from "../Address/AddressEntityInput.vue";
import AddressSelector from "../Address/AddressSelector.vue";
import Errors from "../Errors.vue";
import CartEmptyMessage from "./CartEmptyMessage.vue";
// @ts-nocheck
import CartSummary from './CartSummary.vue';

const cartStore = useCartStore();

interface AddressEntry {
    country: '',
    postal: '',
    number: '',
}

type DeliveryMethod = string
type PaymentMethod = string

interface Props {
    user?: DebtorContactResponse
    paymentMethods?: object[]
    route: string
    loginRoute?: string
    countries: { [key: string]: string },
    errors: object,
    deliveryAddresses?: AddressResponse[],
    invoiceAddresses?: AddressResponse[],
}

const props = withDefaults<Props>(defineProps<Props>(), {})


//https://vueuse.org/core/useStorage/
//DATA saven in session storage, if not filled, fill from user, fill
const form = useStorage('cart-form', {
    email: props.user?.email ?? '',
    payment_method: 'ideal',
    delivery_method: null,
    invoice_address: {},
    delivery_address: {},
    invoice_same_as_delivery: true,
    create_account: true,
    remarks: ''
}, sessionStorage, {mergeDefaults: true})

const csrf: Ref<string | null> = ref('');

const updateCountry = (countryCode) => {
    if (countryCode !== cartStore.delivery_country_code.value) {
        cartStore.updateCountryCode(countryCode);

    }
}

const changeDeliveryMethod = async (delivery) => {
    await cartStore.updateDeliveryMethod({delivery_method: delivery}).then((_response) => {
        //donothing? nu in cart store, potentieel toast melding
    })
}

watch(
    () => form.value.invoice_same_as_delivery,
    (newValue, _oldValue) => {
        if (newValue) {
            updateCountry(form.value.invoice_address?.country);
        } else {
            updateCountry(form.value.delivery_address?.country);
        }
    },
    {deep: true}
);

watch(
    () => form.value.invoice_address,
    (newAddress, _oldAddress) => {
        if (form.value.invoice_same_as_delivery) {
            if (newAddress?.country !== cartStore.delivery_country_code) {
                updateCountry(newAddress?.country);
            }
        }
    },
    {deep: true}
);

watch(
    () => form.value.delivery_address,
    (newAddress, _oldAddress) => {
        if (newAddress?.country !== cartStore.delivery_country_code) {
            updateCountry(newAddress.country);
        }
    },
    {deep: true}
);

onMounted(() => {
    csrf.value = document.querySelector('meta[name="csrf-token"]').getAttribute("content");//set csrf from meta
    if (props.user) {
        //https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Logical_OR_assignment
        form.value.email ||= props.user.email;
        form.value.phone_number ||= props.user?.debtor?.phone;
        form.value.vat_number ||= props.user?.debtor?.vat_number;
        //Preset addressses
        if (props.deliveryAddresses.length && isEmpty(form.value.delivery_address)) {
            form.value.delivery_address = props.deliveryAddresses[0];
        }
        if (props.invoiceAddresses.length && isEmpty(form.value.invoice_address)) {
            form.value.invoice_address = props.invoiceAddresses[0];
        }
    }
    cartStore.loadDeliveryMethods();
})

const invoiceNames = { //field names, important for use in blade forms
    id: "address_invoice",
    company: "company",
    salutation: "salutation",
    first_name: "first_name",
    insertion: "insertion",
    last_name: "last_name",
    postal: "postal",
    house_number: "house_number",
    house_number_addition: "house_number_addition",
    street: "street",
    city: "city",
    country: "country",
    address_line_1: 'address_line_1',
    address_line_2: 'address_line_2',
    type: 'invoice',
}

const deliveryNames = { //field names, important for use in blade forms
    id: "address_delivery",
    company: "delivery_company",
    salutation: "delivery_salutation",
    first_name: "delivery_first_name",
    insertion: "delivery_insertion",
    last_name: "delivery_last_name",
    postal: "delivery_postal",
    house_number: "delivery_house_number",
    house_number_addition: "delivery_house_number_addition",
    street: "delivery_street",
    city: "delivery_city",
    country: "delivery_country",
    address_line_1: 'delivery_address_line_1',
    address_line_2: 'delivery_address_line_2',
    type: 'delivery',
}
</script>
<template>
    <div v-if="cartStore.itemCount === 0 && !cartStore.isLoadingFull" class="py-32">
        <CartEmptyMessage class="max-w-2xl mx-auto"/>
    </div>
    <div class="min-h-screen bg-white lg:bg-[linear-gradient(to_right,white_70%,theme(colors.secondary.100)_70%)] text-slate-700" v-else>
        <form method="POST" :action="route">
            <input v-model="csrf" type="hidden" name="_token"/>

            <div class="mx-auto grid max-w-7xl gap-10 px-4 py-6 lg:grid-cols-[minmax(0,1fr)_360px] lg:px-8 lg:py-10">
                <section class="mx-auto w-full max-w-2xl">
                    <a href="/" class="mb-8 inline-flex items-center rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-medium text-slate-600 transition hover:border-primary hover:text-primary">
                        {{ label('site.cart.continue-shopping') }}
                    </a>

                    <div class="w-full">
                        <h2 class="text-xl lg:text-2xl font-extrabold text-secondary-700">
                            {{ label('site.cart.your_information') }}
                        </h2>
                    </div>

                    <div class="space-y-6 lg:space-y-10 mt-4">
                        <div class="space-y-3">
                            <div class="mt-4 space-y-1" v-if="!user">
                                <label class="mb-2 block text-sm font-semibold text-slate-700">
                                    {{ label('site.account.email') }}
                                    <span class="text-error-500" :title="label('required')">*</span>
                                </label>
                                <input
                                    class="w-full rounded-sm border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                                    type="email" id="email" name="email" autocomplete="email" v-model="form.email" required>
                                <Errors :errors="errors?.email"/>
                                <p class="text-sm text-secondary-700">
                                    {{ label('site.cart.existing_account') }}
                                    <a :href="loginRoute" class="btn btn-link text-secondary-950 font-bold underline">
                                        {{ label('site.button.login') }}
                                    </a>
                                </p>
                            </div>

                            <div class="mt-4 space-y-1">
                                <label class="mb-2 block text-sm font-semibold text-slate-700">
                                    {{ label('site.account.phone') }}
                                    <span class="text-error-500" :title="label('required')">*</span>
                                </label>
                                <input
                                    class="w-full rounded-sm border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                                    type="text" id="phone_number" name="phone_number" v-model="form.phone_number">
                                <Errors :errors="errors?.phone_number"/>
                            </div>
                            <div v-if="form.invoice_address?.company !== '' && form.invoice_address?.company" class="mt-4 space-y-1">
                                <label class="mb-2 block text-sm font-semibold text-slate-700">
                                    {{ label('site.account.vat_number') }}
                                    <span class="text-error-500" :title="label('required')">*</span>
                                </label>
                                <input
                                    class="w-full rounded-sm border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                                    type="text" id="vat_number" name="vat_number" v-model="form.vat_number">

                                <Errors :errors="errors?.vat_number"/>
                            </div>
                        </div>

                        <!-- Shipping address -->
                        <div>
                            <h2 class="text-xl lg:text-2xl font-extrabold text-secondary-700 mb-4">
                                {{ label('site.cart.billing_information') }}
                            </h2>
                            <AddressSelector v-if="user" v-model="form.invoice_address" :countries="countries" type="invoice"
                                              :names="invoiceNames" :options="invoiceAddresses"/>
                            <AddressEntityInput v-else v-model="form.invoice_address" :countries="countries"
                                          :show-address-inputs="true" :show-inputs="true" type="invoice" :names="invoiceNames"
                                          :errors="errors"></AddressEntityInput>
                        </div>

                        <div class="">
                            <h2 class="text-xl lg:text-2xl font-extrabold text-secondary-700">
                                {{ label('site.cart.shipping_information') }}
                            </h2>
                            <div class="mt-4 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
                                <div class="sm:col-span-2">
                                    <div class="flex space-x-2">
                                        <div class="flex h-5 items-center">
                                            <input
                                                v-model="form.invoice_same_as_delivery"
                                                class="appearance-none border w-5 h-5 border-secondary-300 rounded-md shadow-xs checked:bg-secondary-500 checked:text-secondary-500 disabled:bg-secondary-100 disabled:opacity-50 disabled:cursor-not-allowed focus:border-secondary-500 focus:ring-secondary-500  rounded-sm! shadow-none!"
                                                type="checkbox" id="invoice_same_as_delivery" name="invoice_same_as_delivery"
                                                value="1">
                                        </div>
                                        <label class="block font-medium text-sm text-secondary-700"
                                               for="invoice_same_as_delivery"
                                               v-text="label('site.cart.invoice_same_as_delivery')">
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div v-if="form.invoice_same_as_delivery === false" class="py-2 mt-4">
                                <address-selector v-if="user" v-model="form.delivery_address" :countries="countries"
                                                  :names="deliveryNames" :options="deliveryAddresses" type="delivery"/>
                                <AddressEntityInput v-else v-model="form.delivery_address" :countries="countries"
                                                    :show-address-inputs="true" :show-inputs="true" :names="deliveryNames"
                                                    :errors="errors"></AddressEntityInput>
                            </div>
                        </div>

                        <div>
                            <div class="">
                                <fieldset>
                                    <h2 class="text-xl lg:text-2xl font-extrabold text-secondary-700">
                                        {{ label('site.cart.delivery_method') }}
                                    </h2>
                                    <div class="mt-4 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
                                        <input type="hidden" v-model="cartStore.deliveryMethod" name="delivery_method"
                                               id="delivery_method"/>
                                        <label v-for="deliveryMethod in cartStore.deliveryMethods" :class="{
                                                'border-primary! border-2': cartStore.deliveryMethod === deliveryMethod.item_code
                                            }"
                                               @click="changeDeliveryMethod(deliveryMethod.item_code)"
                                               class="relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-xs focus:outline-hidden border-slate-200">
                                            <span class="flex flex-1">
                                                <span class="flex flex-col">
                                                    <span id="delivery-method-5-label"
                                                          class="block text-sm font-medium text-secondary-900"
                                                          v-text="deliveryMethod.title">
                                                    </span>
                                                    <span id="delivery-method-5-description-0"
                                                          class="mt-1 flex items-center text-sm text-secondary-500"
                                                          v-text="deliveryMethod.description">
                                                    </span>
                                                    <span id="delivery-method-5-description-1"
                                                          class="mt-6 text-sm font-medium text-secondary-900"
                                                          v-if="deliveryMethod.price_incl && cartStore.cart">
                                                        {{ Money.format(deliveryMethod.price_incl, cartStore.cart?.currency) }}
                                                    </span>
                                                </span>
                                            </span>
                                            <svg aria-hidden="true" class="h-5 w-5 text-secondary-600 hidden"
                                                 xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                <path fill-rule="evenodd"
                                                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                                                      clip-rule="evenodd"></path>
                                            </svg>
                                        </label>
                                    </div>
                                    <Errors :errors="errors?.delivery_method"/>
                                </fieldset>
                            </div>
                        </div>

                        <div class="">
                            <h2 class="text-xl lg:text-2xl font-extrabold text-secondary-700">
                                {{ label('site.cart.payment') }}
                            </h2>
                            <input type="hidden" name="payment_method" v-model="form.payment_method"/>
                            <fieldset class="mt-4" v-for="paymentOption in paymentMethods">
                                <div class="space-y-4">
                                    <label
                                        @click="form.payment_method = paymentOption.id"
                                        class="relative block cursor-pointer rounded-lg border bg-white px-6 py-4 shadow-xs focus:outline-hidden sm:flex sm:justify-between border-secondary-300"
                                        :class="{
                                            'border-transparent ring-2 ring-secondary-600': paymentOption.id === 'cash_on_delivery',
                                            'border-secondary-300': paymentOption.id !== 'cash_on_delivery',
                                            'border-primary-700! border-2': form.payment_method === paymentOption.id
                                        }"
                                    >
                                        <div class="flex items-center gap-3 w-full">
                                            <div class="w-2/12">
                                                <img class="max-w-10 w-full"
                                                     :src="`data:image/svg+xml;base64, ${paymentOption.image}`"
                                                     alt="">
                                            </div>
                                            <div class="flex flex-col text-sm w-10/12">
                                                <div class="font-medium text-sm text-secondary-900"
                                                     v-text="paymentOption.description">
                                                </div>
                                            </div>
                                        </div>
                                    </label>
                                </div>
                                <Errors :errors="errors?.payment_method"/>
                            </fieldset>
                        </div>

                        <!-- Notes -->
                        <div class="">
                            <div class="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
                                <div class="sm:col-span-2">
                                    <label class="block font-bold text-md text-secondary-700" for="remarks">
                                        {{ label('site.cart.remarks') }} <span
                                        class="font-normal">({{ label('site.cart.optional') }})</span>
                                    </label>
                                    <div class="mt-3">
                                        <textarea
                                            v-model="form.remarks"
                                            class="w-full rounded-sm border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10 min-h-[140px]"
                                            name="remarks" id="remarks"
                                            :placeholder="label('site.cart.remarks.placeholder')"></textarea>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="">
                            <div class="sm:col-span-2">
                                <div class="flex space-x-2">
                                    <div class="flex h-5 items-center">
                                        <input
                                            v-model="form.terms_of_service"
                                            class="appearance-none border w-5 h-5 border-secondary-300 rounded-md shadow-xs checked:bg-secondary-500 checked:text-secondary-500 disabled:bg-secondary-100 disabled:opacity-50 disabled:cursor-not-allowed focus:border-secondary-500 focus:ring-secondary-500  rounded-sm! shadow-none!"
                                            type="checkbox" id="terms_of_service" name="terms_of_service">
                                    </div>
                                    <label class="block font-medium text-sm text-secondary-700" for="terms_of_service"
                                           v-text="label('site.cart.terms_of_service')">
                                    </label>
                                    <span class="text-error-500" :title="label('required')">*</span>
                                </div>
                                <Errors :errors="errors?.terms_of_service"/>
                            </div>
                            <div v-if="!user">
                                <div class="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
                                    <div class="sm:col-span-2">
                                        <div class="flex space-x-2 py-2">
                                            <div class="flex h-5 items-center">
                                                <input
                                                    v-model="form.create_account"
                                                    class="appearance-none border w-5 h-5 border-secondary-300 rounded-md shadow-xs checked:bg-secondary-500 checked:text-secondary-500 disabled:bg-secondary-100 disabled:opacity-50 disabled:cursor-not-allowed focus:border-secondary-500 focus:ring-secondary-500  rounded-sm! shadow-none!"
                                                    type="checkbox" id="create_account" name="create_account" value="1">
                                            </div>
                                            <label class="block font-medium text-sm text-secondary-700" for="create_account"
                                                   v-text="label('site.cart.create_account')">

                                            </label>
                                        </div>
                                        <div v-if="form.create_account"
                                             class="border border-slate-200 rounded-xl bg-gray-100 p-4 flex flex-col gap-4">
                                            <div v-text="label('site.cart.password.description')"
                                                 class="items-center text-sm text-secondary-500"></div>
                                            <input type="password" name="password" :placeholder="label('site.account.password')" autocomplete="off"
                                                   class="appearance-none border border-secondary-300 p-2 rounded-md shadow-xs checked:bg-secondary-500 checked:text-secondary-500 disabled:bg-secondary-100 disabled:opacity-50 disabled:cursor-not-allowed focus:border-secondary-950 focus:ring-secondary-950 block w-full sm:text-sm">
                                            <input type="password" name="password_confirmation" autocomplete="off"
                                                   :placeholder="label('site.account.password_confirmation')"
                                                   class="appearance-none border border-secondary-300 p-2 rounded-md shadow-xs checked:bg-secondary-500 checked:text-secondary-500 disabled:bg-secondary-100 disabled:opacity-50 disabled:cursor-not-allowed focus:border-secondary-950 focus:ring-secondary-950 block w-full sm:text-sm">
                                            <Errors :errors="errors?.password"/>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <CartSummary>
                    <template #actions>
                        <Skeleton class="h-12" v-if="cartStore.isLoading"/>
                        <button type="submit" v-else :disabled="!cartStore.items.length"
                                class="font-medium text-base px-16 py-4 leading-6 text-white transition rounded-lg bg-primary-500 hover:bg-primary-600 hover:duration-500 flex justify-center w-full">
                            {{ label('site.cart.confirm_order') }}
                        </button>
                    </template>
                </CartSummary>
            </div>
        </form>
    </div>
</template>
